// The singleton sound engine: one AudioContext per app, lazily created inside a
// user gesture, gesture-gate tolerant, throttled, and gated by config +
// accessibility + embedding. Components never touch AudioContext directly —
// they call play(role) and the engine owns everything else.

import { config } from './config'
import { ROLES } from './roles'
import type { PlayOptions, SoundRole } from './types'

/** Max one sound per role per this window (dcs-product-sound rule: ~120ms). */
const THROTTLE_MS = 120

let ctx: AudioContext | null = null
let master: GainNode | null = null
let unlockBound = false
/** True once a real user gesture has fired (pointerdown/keydown/touchstart). */
let gestureSeen = false

const lastPlayed = new Map<SoundRole, number>()
const didPlayListeners = new Set<() => void>()

/**
 * True when running inside an iframe/embedded context. Hard-silent there
 * (customer sites render inside the portal editor iframe). Never assume
 * `window.top` is reachable — a cross-origin access throws, which we treat as
 * "embedded" (fail closed to silence).
 */
function isEmbedded(): boolean {
  if (typeof window === 'undefined') {
    return true
  }
  try {
    if (window.frameElement !== null) {
      return true
    }
  } catch {
    return true
  }
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

/** Live check of the OS/browser `prefers-reduced-motion: reduce` signal. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function ensureContext(): AudioContext | null {
  if (typeof window === 'undefined') {
    return null
  }
  if (ctx) {
    return ctx
  }
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) {
    return null
  }
  try {
    ctx = new Ctor()
    master = ctx.createGain()
    master.gain.value = 1
    master.connect(ctx.destination)
  } catch {
    ctx = null
    master = null
  }
  return ctx
}

/**
 * Bind one-time gesture listeners that unlock (resume) the AudioContext on the
 * user's first interaction. Browsers block audio until a gesture; this defers
 * context creation to a valid moment and tolerates rejection silently — no
 * console spam, no retry loop (gesture-gate rule).
 */
export function bindUnlock(): void {
  if (unlockBound || typeof window === 'undefined') {
    return
  }
  unlockBound = true
  const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart']
  const handler = () => {
    gestureSeen = true
    const c = ensureContext()
    if (c && c.state === 'suspended') {
      void c.resume().catch(() => {
        /* still gated — a later gesture will retry via the next event */
      })
    }
    // Self-remove once the context exists and is (or is becoming) running.
    if (c && c.state !== 'suspended') {
      events.forEach((ev) => window.removeEventListener(ev, handler))
    }
  }
  events.forEach((ev) => window.addEventListener(ev, handler, { passive: true }))
}

/** Subscribe to "a sound actually played" (used for the one-time portal hint). */
export function onDidPlay(listener: () => void): () => void {
  didPlayListeners.add(listener)
  return () => didPlayListeners.delete(listener)
}

/**
 * Full gating decision — the single source of truth for "may this sound?".
 * Exported for white-box unit testing of the gate (see engine.spec.ts); it is
 * NOT part of the package's public surface (`index.ts` does not re-export it).
 */
export function shouldPlay(role: SoundRole, opts?: PlayOptions): boolean {
  if (typeof document === 'undefined') {
    return false
  }
  const spec = ROLES[role]
  if (!spec) {
    return false
  }
  // Hard-silent in iframe/embedded contexts (rule beats every preference).
  if (isEmbedded()) {
    return false
  }
  const c = config.value
  if (!c.enabled) {
    return false
  }
  // prefers-reduced-motion auto-mutes UNLESS the user explicitly ENABLED sound
  // while the signal was active (prmOverride). Merely muting, unmuting while
  // PRM was off, or tweaking a category never defeats the auto-mute.
  if (prefersReducedMotion() && !c.prmOverride) {
    return false
  }
  const category = spec.category
  if (!c.categories[category]) {
    return false
  }
  // Suppress non-notification roles while the tab is hidden; notification roles
  // are exactly the "you were in another tab" case, so they are allowed.
  if (document.hidden && category !== 'notification') {
    return false
  }
  if (!opts?.force) {
    const last = lastPlayed.get(role) ?? 0
    if (performance.now() - last < THROTTLE_MS) {
      return false
    }
  }
  return true
}

/**
 * Request a sound by semantic role. Returns true only when a sound was actually
 * emitted (all gates passed AND the context was running). Never throws.
 */
export function play(role: SoundRole, opts?: PlayOptions): boolean {
  if (!shouldPlay(role, opts)) {
    return false
  }
  // Never construct an AudioContext outside a user gesture (e.g. a WS-driven
  // toast before any interaction) — Chrome logs an autoplay warning for that.
  // Bail silently; the unlock handler creates the context on the first gesture.
  if (!ctx && !gestureSeen) {
    return false
  }
  const c = ensureContext()
  if (!c || !master) {
    return false
  }
  // If still suspended (no gesture yet), attempt an async resume and bail for
  // now — we cannot emit synchronously before the gate lifts. A later, post-
  // gesture call will succeed. This keeps the very first pre-interaction event
  // silent instead of throwing or queuing a surprise chime.
  if (c.state === 'suspended') {
    void c.resume().catch(() => {})
    return false
  }

  const spec = ROLES[role]
  lastPlayed.set(role, performance.now())
  const now = c.currentTime
  const finalGain = Math.min(0.8, spec.gain * config.value.volume)
  try {
    spec.render(c, master, now, finalGain)
  } catch {
    return false
  }

  didPlayListeners.forEach((fn) => {
    try {
      fn()
    } catch {
      /* listener errors must not break playback */
    }
  })
  return true
}

/** Test/diagnostic hook: is the context live and running? */
export function isRunning(): boolean {
  return ctx?.state === 'running'
}
